import React, { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import {
  getFilterOptions,
  IFilterField
} from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import format from 'date-fns/format'
import {
  DataCDROverviewType,
  DataCDRPackageDetailType
} from 'types/api/faber/dataUsage.type'

import DataUsageTooltip from './DataUsageTooltip'

const usageColumns = [
  {
    field: 'Timestamp',
    headerName: 'Timestamp',
    sortable: false,
    minWidth: 150,
    valueFormatter: ({ value }) =>
      value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
  },
  { field: 'CDRID', headerName: 'CDRID', sortable: false, minWidth: 300 },
  { field: 'IMSI', headerName: 'IMSI', sortable: false, minWidth: 50 },
  {
    field: 'DataUsedMB',
    headerName: 'Data Used MB',
    sortable: false,
    minWidth: 100
  },
  { field: 'Country', headerName: 'Country', sortable: false, minWidth: 100 },
  { field: 'Network', headerName: 'Network', sortable: false, minWidth: 100 }
]

function DataAllocationLog({ overviewRows, detailedRows }: DataRolloverProps) {
  const [countryChecked, setCountryChecked] = useState<string[]>([])
  const [networkChecked, setNetworkChecked] = useState<string[]>([])
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [filteredRows, setFilteredRows] = useState<DataCDROverviewType[]>([])

  const columns = [
    {
      field: 'Timestamp',
      headerName: 'Timestamp',
      sortable: true,
      valueFormatter: ({ value }) =>
        value ? format(new Date(value), 'dd MMM yyyy HH:mm') : ''
    },
    {
      field: 'Type',
      headerName: 'Type',
      sortable: false,
      width: 150,
      renderCell: (params: { row: DataCDROverviewType }) => {
        if (params.row.Type === 'Customer Usage') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextColorFontBody>{params.row.Type}</TextColorFontBody>
              <DataUsageTooltip
                usageRows={detailedRows ? detailedRows : []}
                usageColumns={usageColumns}
                calculateTotal={false}
                toolbar={false}
              />
            </Box>
          )
        }
      }
    },
    {
      field: 'ExpiryDate',
      headerName: 'Expiry Date',
      sortable: false,
      valueFormatter: ({ value }) =>
        value ? format(new Date(value), 'dd MMM yyyy HH:mm') : '',
      renderCell: (params: { row: DataCDROverviewType }) => {
        if (
          params.row.Type === 'Lite Plan' ||
          params.row.Type === 'Pro Plan' ||
          params.row.Type === 'Customer Usage'
        ) {
          return ''
        }
      }
    },
    {
      field: 'DataAllocatedMB',
      headerName: 'Data Allocated (MB)',
      sortable: false,
      width: 50,
      renderCell: (params: { row: DataCDROverviewType }) => {
        if (params.row.Type === 'Customer Usage') {
          return ''
        }
      }
    },
    {
      field: 'DataUsedMB',
      headerName: 'Data Used (MB)',
      sortable: false,
      width: 50
    },
    { field: 'Country', headerName: 'Country', sortable: false },
    { field: 'Network', headerName: 'Network', sortable: false }
  ]

  const filterFields = useMemo(() => {
    const tempFilterFields: IFilterField[] = [
      {
        values: getFilterOptions(overviewRows.map((row) => row.Country)),
        checked: countryChecked,
        setChecked: setCountryChecked,
        name: 'Country',
        index: 'Country'
      },
      {
        values: getFilterOptions(overviewRows.map((row) => row.Network)),
        checked: networkChecked,
        setChecked: setNetworkChecked,
        name: 'Network',
        index: 'Network'
      }
    ]
    return tempFilterFields
  }, [overviewRows, networkChecked, countryChecked, filteredRows])

  return (
    <Box sx={{ height: '300px', display: 'flex' }}>
      <MUIDataGrid
        getRowId={(row) => row.Timestamp + row.data + overviewRows.indexOf(row)}
        columns={columns}
        rows={filterActive ? filteredRows : overviewRows}
        tableHeight='280px'
        rowHeight={40}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        pagination
        components={{
          Toolbar: Toolbar
        }}
        componentsProps={{
          toolbar: {
            filterProps: {
              filterFields: filterFields,
              filterActive,
              setFilterActive,
              rows: overviewRows,
              setFilteredRows
            }
          }
        }}
        sx={{
          '& .MuiDataGrid-iconSeparator': {
            visibility: 'hidden'
          },
          '& .MuiDataGrid-footerContainer': {
            border: '0'
          },
          '& .MuiDataGrid-cellContent': {
            fontSize: '14px'
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '14px',
            fontWeight: '500'
          },
          '& .MuiDataGrid-cell': {
            border: '0px'
          },
          '& .MuiDataGrid-columnHeaders': {
            border: '0px'
          },
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
            outline: 'none'
          },
          mx: '-10px'
        }}
      />
    </Box>
  )
}

export default DataAllocationLog

interface DataRolloverProps {
  overviewRows: DataCDROverviewType[]
  detailedRows: DataCDRPackageDetailType[]
}
